import React from 'react';
import { X } from 'lucide-react';

// [EXPORT WIDTH COMPENSATION — STOPGAP] Font sizes are multiplied by
// scrollExportScalingFactor on export, but block widths are percentages of
// the container, which know nothing about that factor. The two only agree
// when the preview pane happens to be ~1920/0.73 ≈ 2630 CSS px wide — resize
// the window and preview drifts too. Logo blocks already compensate for this
// via blockWidthAdjustment (1.1); multi-name text blocks had no equivalent,
// so their columns rendered ~27% narrow on export and long names wrapped
// that shouldn't. Measured from a 1080p export vs. the in-app preview: the
// name grid occupied ~65% of frame width where the preview showed ~89%.
// 0.89/0.65 ≈ 1.37 ≈ 1/0.73. Only 1920 is measured — 1280/2560/3840 are
// placeholders (1.0) until verified against a real render at that resolution.
// This is a stopgap tied to the current absolute-scaled/percentage-based
// layout split; the real fix is the shared fixed-stage refactor (see notes
// elsewhere), which will make this table unnecessary.
const MULTI_BLOCK_EXPORT_WIDTH_ADJUSTMENT = {
    1280: 1.0,
    1920: 1.0, // was 1.37 — compensated for the doubled export safe margin, now fixed at the source
    2560: 1.0,
    3840: 1.0,
};

// Width (CSS px) of the preview's layout stage. In a 1080p export, text is
// drawn at fontSize x 0.73 (scroll/card export scale) x 2 (deviceScaleFactor)
// inside a 1920px frame, so a preview font size f fits the frame exactly like
// the export when the frame is 1920 / 1.46 ≈ 1315px wide. 4K renders at 1080p
// and upscales, so it matches too. (720p and 1440p use a 0.5 / 1.0 scale,
// i.e. a 1280px stage — about 3% roomier than this, never tighter.)
export const PREVIEW_STAGE_WIDTH = 1920 / (2 * 0.73);

const FONT_DATA = {
    'arial': { name: 'Arial', fallback: 'sans-serif', variants: { '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'courier-new': { name: 'Courier New', fallback: 'monospace', variants: { '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'eb-garamond': { name: 'EB Garamond', fallback: 'serif', variants: { '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'futura': { name: 'Futura', fallback: 'sans-serif', variants: { '300_normal': { weight: '300', style: 'normal' }, '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '300_italic': { weight: '300', style: 'italic' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'garamond': { name: 'Garamond', fallback: 'serif', variants: { '300_normal': { weight: '300', style: 'normal' }, '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '300_italic': { weight: '300', style: 'italic' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'georgia': { name: 'Georgia', fallback: 'serif', variants: { '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'helvetica': { name: 'Helvetica', fallback: 'sans-serif', variants: { '300_normal': { weight: '300', style: 'normal' }, '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '300_italic': { weight: '300', style: 'italic' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'helvetica-rounded': { name: 'Helvetica Rounded', fallback: 'sans-serif', variants: { '700_normal': { weight: '700', style: 'normal' }}},
    'optima': { name: 'Optima', fallback: 'sans-serif', variants: { '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'palatino': { name: 'Palatino', fallback: 'serif', variants: { '300_normal': { weight: '300', style: 'normal' }, '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '300_italic': { weight: '300', style: 'italic' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}},
    'poppins': { name: 'Poppins', fallback: 'sans-serif', variants: { '100_normal': { weight: '100', style: 'normal' }, '300_normal': { weight: '300', style: 'normal' }, '400_normal': { weight: '400', style: 'normal' }, '700_normal': { weight: '700', style: 'normal' }, '300_italic': { weight: '300', style: 'italic' }, '400_italic': { weight: '400', style: 'italic' }, '700_italic': { weight: '700', style: 'italic' }}}
};

// [WEIGHT COERCION FIX] resolveFontProperties compared weights with
// parseInt(), which returns NaN for CSS keywords like 'bold'. Every comparison
// in the closest-weight reduce was then `NaN < NaN` === false, so the reduce
// always returned its first element — for Futura that is '300_normal'. Asking
// for bold silently produced Futura LIGHT. Normalize keywords to numbers before
// any comparison so the variant lookup and the fallback both behave.
const WEIGHT_ALIASES = {
    thin: 100, extralight: 200, ultralight: 200, light: 300, book: 400,
    normal: 400, regular: 400, medium: 500, semibold: 600, demibold: 600,
    bold: 700, extrabold: 800, ultrabold: 800, black: 900, heavy: 900,
};

const toNumericWeight = (w, fallback = 400) => {
    if (typeof w === 'number' && Number.isFinite(w)) return w;
    const s = String(w ?? '').trim().toLowerCase().replace(/[\s\-_]/g, '');
    if (WEIGHT_ALIASES[s] !== undefined) return WEIGHT_ALIASES[s];
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : fallback;
};

const resolveFontProperties = (familyKey = 'poppins', requestedWeight = '400', requestedStyle = 'normal') => {
    // If it's potentially a custom font, just return the requested values
    if (!FONT_DATA[familyKey]) {
        return {
            fontFamily: `"${familyKey}"`, // Assume custom fonts need quotes
            fontWeight: requestedWeight,
            fontStyle: requestedStyle,
        };
    }

    const familyData = FONT_DATA[familyKey];
    // Coerce first: FONT_DATA variant keys are numeric ('700_normal'), so a
    // keyword weight like 'bold' would never match an exact variant and would
    // fall into the (previously broken) closest-weight path.
    const numericWeight = toNumericWeight(requestedWeight);
    const variantKey = `${numericWeight}_${requestedStyle}`;

    if (familyData.variants[variantKey]) {
        const variant = familyData.variants[variantKey];
        return {
            fontFamily: `"${familyData.name}", ${familyData.fallback}`,
            fontWeight: variant.weight,
            fontStyle: variant.style,
        };
    }

    // Fallback logic if the exact weight/style combo doesn't exist
    // Try matching style first, then find closest weight
    const styleMatchKeys = Object.keys(familyData.variants).filter(key => key.endsWith(`_${requestedStyle}`));
    if (styleMatchKeys.length > 0) {
        // Find the weight closest to the requested one among those with the correct style
        const closestWeightKey = styleMatchKeys.reduce((prev, curr) => {
            const prevWeight = parseInt(prev.split('_')[0], 10);
            const currWeight = parseInt(curr.split('_')[0], 10);
            // numericWeight is guaranteed finite, so these comparisons can no
            // longer collapse to NaN and silently return the lightest variant.
            return Math.abs(currWeight - numericWeight) < Math.abs(prevWeight - numericWeight) ? curr : prev;
        });
        const variant = familyData.variants[closestWeightKey];
        return {
            fontFamily: `"${familyData.name}", ${familyData.fallback}`,
            fontWeight: variant.weight,
            fontStyle: variant.style,
        };
    }

    // Absolute fallback: Use 400 normal or the first available variant
    const fallbackKey = familyData.variants['400_normal'] ? '400_normal' : Object.keys(familyData.variants)[0];
    const variant = familyData.variants[fallbackKey];
    return {
        fontFamily: `"${familyData.name}", ${familyData.fallback}`,
        fontWeight: variant.weight,
        fontStyle: variant.style,
    };
};


// --- Effective per-credit typography (ONE source of truth) ----------------------
// Used by the renderer below AND by the Inspector in App.js, so the style panel
// always shows exactly what is drawn on screen / in the export.
//
// Why the filtering: credits created by Paste List / CSV import were seeded with
// placeholder style values (fontFamily: 'default', fontWeight: 'normal',
// lineHeight: 1.5, textAlign: 'center', ...). Those were harmless until the first
// time anything was changed in that credit's Style tab, which flips
// `override: true` — after which every seed suddenly applied. fontFamily
// 'default' isn't a real font, so the block fell back to the browser serif
// (Times), and lineHeight 1.5 / weight 'normal' replaced the globals. That is the
// "multi-name doesn't follow the defaults" bug. Seeds and sentinels are now
// treated as "not overridden", and fields that have no per-credit control at all
// (line height, letter spacing, decoration) always come from the globals.
const STYLE_SENTINELS = new Set(['', 'default', 'inherit', 'global']);
const GLOBAL_ONLY_FIELDS = new Set(['lineHeight', 'letterSpacing', 'textDecoration']);
const SEED_VALUES = { fontWeight: ['normal'], fontStyle: ['normal'] };

export const getCreditOverride = (credit, field) => {
    if (!credit || credit.override !== true) return undefined;
    if (GLOBAL_ONLY_FIELDS.has(field)) return undefined;
    const v = credit[field];
    if (v === undefined || v === null) return undefined;
    if (typeof v === 'number' && (!Number.isFinite(v) || v === 0)) return undefined;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (STYLE_SENTINELS.has(s)) return undefined;
        if (SEED_VALUES[field] && SEED_VALUES[field].includes(s)) return undefined;
    }
    return v;
};

export const resolveCreditTypography = (credit = {}, settings = {}, isCard = false) => {
    const o = (f) => getCreditOverride(credit, f);
    const globalFamily = (isCard
        ? (settings.cardFontFamily || settings.fontFamily)
        : (settings.scrollFontFamily || settings.fontFamily)) || 'poppins';
    const pick = (field, fallback) => {
        const v = o(field);
        return { value: v !== undefined ? v : fallback, overridden: v !== undefined };
    };
    const baseSize = settings.fontSize || 16;
    return {
        fontFamily: pick('fontFamily', globalFamily),
        fontWeight: pick('fontWeight', settings.defaultFontWeight || '400'),
        fontStyle: pick('fontStyle', settings.defaultFontStyle || 'normal'),
        fontSize: pick('fontSize', baseSize),                                  // single lines + spacing reference
        headerFontSize: pick('fontSize', settings.headerFontSize || baseSize), // 'title' headers
        headerFontWeight: pick('fontWeight', settings.headerFontWeight || settings.defaultFontWeight || '400'),
        roleFontSize: pick('roleFontSize', settings.roleFontSize || baseSize),
        roleFontWeight: pick('roleFontWeight', settings.roleFontWeight || settings.defaultFontWeight || '400'),
        nameFontSize: pick('nameFontSize', settings.nameFontSize || baseSize),
        nameFontWeight: pick('nameFontWeight', settings.nameFontWeight || settings.defaultFontWeight || '400'),
        roleTransform: pick('roleTransform', settings.roleCaps || 'normal'),
        nameTransform: pick('nameTransform', settings.nameCaps || 'normal'),
        textAlign: pick('textAlign', settings.textAlign || 'center'),
        roleColor: pick('roleColor', settings.roleColor),
        nameColor: pick('nameColor', settings.nameColor),
        blockNameColor: pick('blockNameColor', settings.blockNameColor),
        textDecoration: { value: settings.textDecoration || 'none', overridden: false },
        lineHeight: { value: settings.lineHeight || 1.5, overridden: false },
        letterSpacing: { value: settings.letterSpacing || 0, overridden: false },
    };
};

// The weight that is ACTUALLY drawn for a family (after snapping to the
// family's available variants), as a numeric string — for the Inspector.
export const getRenderedWeightKey = (familyKey, weight, style = 'normal') => {
    const resolved = resolveFontProperties(familyKey, weight, style);
    return String(toNumericWeight(resolved.fontWeight));
};

// Multi-name header -> names spacing. Default ("Match Headers") uses exactly the
// formula a regular header credit uses for the gap below it:
//   settings.fontSize * settings.blockSpacing * 0.8 px
// credit.headerSpacing (same units as the global "Space Between Credits") is the
// per-block adjustment. Legacy credit.roleSpacing (a multiplier over 0.5rem)
// is still honored for blocks that set it before this existed.
export const resolveMultiHeaderSpacing = (credit = {}, settings = {}) => {
    if (credit.headerSpacing !== undefined && credit.headerSpacing !== null && credit.headerSpacing !== '') {
        return { mode: 'custom', multiplier: Number(credit.headerSpacing) };
    }
    if (credit.roleSpacing !== undefined && credit.roleSpacing !== null && credit.roleSpacing !== '') {
        return { mode: 'legacy', rem: Number(credit.roleSpacing) * 0.5 };
    }
    return { mode: 'match', multiplier: Number(settings.blockSpacing ?? 2) };
};

// A manual "move to the next line without starting a new grid item" break.
// Used by the 'multiple' name grid: plain Enter still splits into a new
// name/grid item (unchanged), Shift+Enter inserts this marker instead —
// a soft break staying part of the same entry. Rendered as <br/> in static
// and export contexts, but kept as the literal character inside editable
// NameCells (pre-wrap renders it as a line break) so innerText round-trips
// it instead of collapsing it into '\n', the new-entry delimiter. Uses
// the Unicode line separator (U+2028) since it's never typed naturally
// and survives .innerText round-trips, unlike '\n' which is already the
// "new entry" delimiter for this credit type.
const SOFT_BREAK = '\u2028';

// --- Song Credits mode (a variant of the 'multiple' block) -----------------
// A Song Credits block reuses the same storage shape as an ordinary
// Multi-Name Block (credit.name, newline-delimited) so every existing
// pipeline that already treats credit.name as opaque text — duration
// estimation, minimal export payloads, CSV/NLE export, search filtering —
// keeps working unmodified. Each line just carries TWO fields instead of
// one, joined by a literal tab: "Song Title<TAB>Written/Performed/Published
// info". Tab is the same delimiter Batch Add's smart-paste parser already
// uses for Role/Name pairs (see BATCH_SAMPLE_SCROLL / parseBatchPasteToRows
// in App.js), so this isn't a new convention — it's the existing one,
// reused. The Song Board (App.js) is the only place that writes this
// format; this parser is the only place that reads it back.
const SONG_FIELD_SEP = '\t';
const parseSongEntries = (nameField) => {
    const lines = (nameField || '').split('\n');
    return lines.map(line => {
        const idx = line.indexOf(SONG_FIELD_SEP);
        return idx === -1
            ? { title: line, info: '' }
            : { title: line.slice(0, idx), info: line.slice(idx + 1) };
    });
};

// --- Imperative editing layer -----------------------------------------------
// Every editable text region in this file is an ImperativeEditable: React
// renders the contentEditable element EMPTY and never any children into it.
// The text is owned imperatively (textContent) and synced only while the
// element is NOT focused. While the user types, the browser may split/merge
// text nodes freely and React has nothing to reconcile against — which is
// the class of bug that previously crashed the editor and duplicated lines
// (e.g. editing a multi-name role re-rendered React children under a focused
// contentEditable and doubled the text).
//
// We also guarantee the element only ever contains TEXT NODES:
//   - Enter/paste insertion goes through insertTextNodeAtCaret (never
//     execCommand, which can materialize '\n' as <br> in Chrome)
//   - Cmd/Ctrl+B/I/U are blocked so no rich-text elements appear
// so innerText/Range.toString() reads are always faithful.

// Insert plain text as a literal text node at the caret inside `el`.
const insertTextNodeAtCaret = (el, text) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !el.contains(selection.getRangeAt(0).startContainer)) {
        el.appendChild(document.createTextNode(text));
        return;
    }
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};

// Text before/after the caret within `el` (display space; '\n' included).
const getCaretSplit = (el) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.startContainer)) return null;
    const pre = document.createRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.startContainer, range.startOffset);
    const post = document.createRange();
    post.selectNodeContents(el);
    post.setStart(range.endContainer, range.endOffset);
    return { before: pre.toString(), after: post.toString(), collapsed: range.collapsed };
};

const readEditableText = (el) =>
    ((typeof el.innerText === 'string') ? el.innerText : el.textContent || '')
        .replace(/\r/g, '')
        .replace(/\n+$/, ''); // browsers append a trailing newline to innerText

// --- Name gaps ----------------------------------------------------------------
// A run of 2+ spaces is the user's "gap here" marker (e.g. between two names on
// a title card, optionally reserving room for an "&" graphic). How wide that gap
// renders is controlled per card:
//   gapEm == null  -> "Natural": exactly as wide as the typed spaces
//   gapEm >= 0     -> "Custom":  a fixed width in em of that line's font size,
//                     no matter how many spaces were typed (2 or 5 = same gap)
// Either way the run is wrapped in an inline-block so Chromium's paint and
// intrinsic-size passes agree on its width (the original centering fix). The
// wrapper CONTAINS the real spaces, so textContent / copy / caret offsets are
// unchanged — only the rendered width differs. The static renderer
// (EditableCreditText, used during playback and export) and the in-editor
// preview (ImperativeEditable, when the field isn't focused) share this, so
// the editor preview and the export always show the same gap.
const GAP_RUN_SPLIT_RE = /( {2,})/;
const GAP_RUN_TEST_RE = / {2,}/;
const hasGapRun = (text) => GAP_RUN_TEST_RE.test(text || '');
const normalizeGapEm = (gapEm) => {
    if (gapEm === null || gapEm === undefined || gapEm === '') return null;
    const n = Number(gapEm);
    return Number.isFinite(n) && n >= 0 ? n : null;
};
const gapSpacerStyle = (gapEm) => (gapEm === null
    ? { display: 'inline-block', whiteSpace: 'pre' }
    : { display: 'inline-block', whiteSpace: 'pre', width: `${gapEm}em` });

// DOM version of the spacer for ImperativeEditable (which owns its DOM directly).
const renderGapDecoratedInto = (el, text, gapEm) => {
    el.textContent = '';
    const style = gapSpacerStyle(gapEm);
    text.split(GAP_RUN_SPLIT_RE).forEach((chunk) => {
        if (!chunk) return;
        if (/^ {2,}$/.test(chunk)) {
            const span = document.createElement('span');
            span.setAttribute('data-name-gap', '');
            Object.assign(span.style, style);
            span.textContent = chunk;
            el.appendChild(span);
        } else {
            el.appendChild(document.createTextNode(chunk));
        }
    });
};

// Put a selection back at character offsets inside an element that holds a
// single text node (i.e. right after flattening).
const setSelectionOffsets = (el, start, end) => {
    const node = el.firstChild;
    if (!node) return;
    const len = node.textContent.length;
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.setStart(node, Math.max(0, Math.min(start, len)));
    range.setEnd(node, Math.max(0, Math.min(end, len)));
    selection.removeAllRanges();
    selection.addRange(range);
};

const ImperativeEditable = ({
    as: Tag = 'div',
    value = '',
    transform,
    displayValue,          // optional: pre-computed display string (overrides value+transform)
    className,
    style,
    onCommit,              // (rawDisplayText) => void — called on blur unless suppressed
    onEditStart,
    onEditEnd,
    onSpecialKey,          // optional (event, api) => boolean; return true if handled
    onPasteText,           // optional (plainText, api) => boolean; return true if handled
    encodeSoftBreaksOnCopy = false, // when true, a copied/cut selection's real '\n' (this field's soft breaks — see SOFT_BREAK) is written to the clipboard as the SOFT_BREAK marker instead, so pasting it elsewhere is recognized as one entry's internal line breaks rather than a list of separate entries to split apart
    shouldFocus = false,   // focus this element after render (caret at focusOffset)
    focusOffset = 0,
    onFocusHandled,
    gapEm,                 // optional: custom name-gap width in em (see "Name gaps"); undefined/null = natural
}) => {
    const ref = React.useRef(null);
    const isEditingRef = React.useRef(false);
    const suppressCommitRef = React.useRef(false);
    const pointerDownRef = React.useRef(false);
    // What the DOM currently shows while NOT editing: `${text}\u0000${gap}`.
    // null = the DOM is plain text and must be (re)decorated on the next sync.
    const renderedKeyRef = React.useRef(null);

    const display = (displayValue !== undefined && displayValue !== null)
        ? displayValue
        : applyTextTransformStatic(value || '', transform);

    const customGapEm = normalizeGapEm(gapEm);
    const customGapEmRef = React.useRef(customGapEm);
    customGapEmRef.current = customGapEm;

    // Render `text` into the element while it is NOT being edited. With a
    // custom gap, gap runs become fixed-width spacers so the idle editor
    // preview matches the export exactly. With no custom gap it stays plain
    // text (raw spaces are already their natural width).
    const renderIdle = (el, text) => {
        const gap = customGapEmRef.current;
        const decorate = gap !== null && hasGapRun(text);
        const key = `${text}\u0000${decorate ? gap : ''}`;
        if (renderedKeyRef.current === key && el.textContent === text) return;
        if (decorate) renderGapDecoratedInto(el, text, gap);
        else el.textContent = text;
        renderedKeyRef.current = key;
    };

    // While editing, the element must hold ONLY plain text (see the notes
    // above), so spacers are removed the moment editing starts. The spacers
    // contain the real spaces, so character offsets map 1:1 and the caret /
    // selection is carried over unchanged. The gap shows at its natural width
    // while you type and snaps back to the card's gap width when you click away.
    const flattenForEditing = () => {
        const el = ref.current;
        renderedKeyRef.current = null;
        if (!el || !el.querySelector('[data-name-gap]')) return;
        const split = getCaretSplit(el);
        const text = el.textContent;
        el.textContent = text;
        if (split && document.activeElement === el) {
            setSelectionOffsets(el, split.before.length, text.length - split.after.length);
        }
    };

    // Sync DOM to committed value — never under the user's caret — then honor
    // any pending focus request (used after splits/merges to land the caret
    // in the right cell at the right offset).
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!isEditingRef.current) {
            renderIdle(el, display);
        }
        if (shouldFocus) {
            if (el.querySelector('[data-name-gap]')) {
                el.textContent = el.textContent;
                renderedKeyRef.current = null;
            }
            try {
                el.focus();
                let node = el.firstChild;
                if (!node) { node = document.createTextNode(''); el.appendChild(node); }
                const offset = Math.max(0, Math.min(focusOffset, node.textContent.length));
                const selection = window.getSelection();
                if (selection) {
                    const range = document.createRange();
                    range.setStart(node, offset);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            } catch (err) { /* caret placement is best-effort */ }
            if (onFocusHandled) onFocusHandled();
        }
    });

    const api = {
        element: () => ref.current,
        getText: () => (ref.current ? readEditableText(ref.current) : ''),
        getSplit: () => (ref.current ? getCaretSplit(ref.current) : null),
        isCollapsedAtStart: () => {
            const split = ref.current && getCaretSplit(ref.current);
            return !!(split && split.collapsed && split.before.length === 0);
        },
        isCollapsedAtEnd: () => {
            const split = ref.current && getCaretSplit(ref.current);
            return !!(split && split.collapsed && split.after.length === 0);
        },
        insert: (text) => { if (ref.current) insertTextNodeAtCaret(ref.current, text); },
        // Call before committing a structural change (split/merge) from a key
        // handler: allows the resync effect to touch this element again and
        // makes the imminent blur (focus moving to another cell) a no-op so
        // the structural commit isn't clobbered by a stale full-text commit.
        suppressNextCommit: () => { suppressCommitRef.current = true; isEditingRef.current = false; },
    };

    // See encodeSoftBreaksOnCopy above. Only intervenes when the selection
    // actually spans a line break — a plain single-line copy is left to the
    // browser's default clipboard handling untouched.
    const handleCopyOrCut = (e) => {
        if (!encodeSoftBreaksOnCopy) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const selected = selection.toString();
        if (!selected.includes('\n')) return;
        e.preventDefault();
        e.clipboardData.setData('text/plain', selected.split('\n').join(SOFT_BREAK));
        if (e.type === 'cut') {
            // preventDefault() also suppresses the browser's own removal of
            // the selected content, so delete it ourselves.
            const range = selection.getRangeAt(0);
            range.deleteContents();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    return (
        <Tag
            ref={ref}
            contentEditable
            suppressContentEditableWarning
            className={className}
            style={style}
            onMouseDown={() => {
                pointerDownRef.current = true;
                window.addEventListener('mouseup', () => { pointerDownRef.current = false; }, { once: true });
            }}
            onFocus={() => {
                isEditingRef.current = true;
                if (ref.current && ref.current.querySelector('[data-name-gap]')) {
                    if (pointerDownRef.current) {
                        // Mouse focus: let the click finish placing the caret (or a
                        // drag finish selecting) against the spacer DOM first, then
                        // flatten and carry that caret/selection over.
                        window.addEventListener('mouseup', () => setTimeout(() => {
                            if (isEditingRef.current) flattenForEditing();
                        }, 0), { once: true });
                    } else {
                        flattenForEditing();
                    }
                } else {
                    renderedKeyRef.current = null;
                }
                if (onEditStart) onEditStart();
            }}
            onCopy={handleCopyOrCut}
            onCut={handleCopyOrCut}
            onBlur={(e) => {
                isEditingRef.current = false;
                if (onEditEnd) onEditEnd();
                if (suppressCommitRef.current) { suppressCommitRef.current = false; return; }
                const text = readEditableText(e.target);
                if (onCommit) onCommit(text);
                // Re-apply the gap spacers right away (don't wait for a re-render,
                // which won't happen if the text didn't change).
                if (ref.current && document.activeElement !== ref.current) renderIdle(ref.current, text);
            }}
            onKeyDown={(e) => {
                // Block rich-text shortcuts that would inject non-text nodes.
                if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
                    e.preventDefault();
                    return;
                }
                if (onSpecialKey && onSpecialKey(e, api)) return;
                if (e.key === 'Enter') {
                    // Default: a literal newline character (pre/pre-wrap renders it).
                    e.preventDefault();
                    api.insert('\n');
                }
            }}
            onPaste={(e) => {
                e.preventDefault();
                const text = (e.clipboardData || window.clipboardData).getData('text/plain');
                if (onPasteText && onPasteText(text, api)) return;
                api.insert(text);
                // No immediate commit needed: React never resets a focused
                // element's DOM, so uncommitted text can't be lost — blur commits.
            }}
        />
    );
};

const applyTextTransformStatic = (text, transform) => {
    if (!text) return '';
    switch (transform) {
        case 'uppercase': return text.toUpperCase();
        case 'lowercase': return text.toLowerCase();
        default: return text;
    }
};

// --- Why this exists ---
// Every editable field in this file lives inside a contentEditable div —
// React renders its initial content, but from that point on the browser
// owns and mutates that DOM subtree directly as the person types (React
// isn't told about any of it; these fields only report back to state on
// blur/paste). That's fine as long as nothing else re-renders that subtree
// in the meantime. But this app has plenty of things that can trigger an
// unrelated re-render while someone is mid-edit — an autosave tick, the
// scroll/card preview animating, any other state update anywhere in the
// editor — and when that happens, React reconciles its last-known virtual
// DOM for this text against the live DOM, which the browser has since
// restructured (deleting a space commonly merges or splits text nodes).
// React ends up calling removeChild/insertBefore against nodes that no
// longer exist where it expects them, which is the crash.
//
// Wrapping the actual text content in its own React.memo'd component fixes
// this: value/transform don't change until blur (this field's own edits
// aren't committed to state yet), so React.memo bails out of touching this
// subtree at all for the entire duration of the edit — regardless of how
// many unrelated re-renders happen around it. Once the field blurs, focus
// has already moved on, the browser stops mutating it, and the next render
// (now with fresh, committed props) is safe.
//
// Defined at module scope (not inside CreditPreview) so React.memo's
// identity actually persists across renders instead of being recreated
// every time.
const EditableCreditText = React.memo(function EditableCreditText({ value, transform, parseGuildBrackets = false, gapEm }) {
    if (!value) {
        return <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />;
    }

    const transformed = applyTextTransformStatic(value, transform);

    // Split on manual soft-breaks first, then run bracket parsing (or just
    // the plain transformed text) within each resulting segment.
    const lines = transformed.split(SOFT_BREAK);

    // Multiple consecutive spaces are a deliberate name gap — see "Name gaps"
    // near the top of this file. The run is wrapped in an inline-block (fixes
    // the off-center bug where Chromium's intrinsic-size pass and paint pass
    // disagreed on a raw space run's width) that CONTAINS the real spaces:
    //   - Natural (gapEm null): width = the typed spaces in this font, exactly
    //     what the editor shows. (Previously a hidden 0.6em-per-space constant,
    //     which made the export gap 2-3x wider than the editor preview.)
    //   - Custom: a fixed `gapEm` em, set per card in the Inspector, identical
    //     to what ImperativeEditable renders in the idle editor preview.
    const gapStyle = gapSpacerStyle(normalizeGapEm(gapEm));
    const splitGapSpacers = (text, keyPrefix) => {
        const chunks = text.split(GAP_RUN_SPLIT_RE);
        if (chunks.length === 1) return text;
        return chunks.map((chunk, gi) => {
            if (/^ {2,}$/.test(chunk)) {
                return (
                    <span key={`${keyPrefix}-gap-${gi}`} data-name-gap="" style={gapStyle}>
                        {chunk}
                    </span>
                );
            }
            return chunk;
        });
    };

    const renderSegment = (segment, keyPrefix) => {
        if (!parseGuildBrackets) return splitGapSpacers(segment, keyPrefix);
        const parts = segment.split(/(\[.*?\])/g);
        return parts.map((part, i) => {
            if (part.startsWith('[') && part.endsWith(']')) {
                return (
                    <span key={`${keyPrefix}-${i}`} style={{ fontSize: '0.7em', verticalAlign: 'baseline' }}>
                        {part.slice(1, -1)}
                    </span>
                );
            }
            return <React.Fragment key={`${keyPrefix}-${i}`}>{splitGapSpacers(part, `${keyPrefix}-${i}`)}</React.Fragment>;
        });
    };

    if (lines.length === 1) {
        return renderSegment(lines[0], 0);
    }

    return (
        <>
            {lines.map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {renderSegment(line, i)}
                </React.Fragment>
            ))}
        </>
    );
});

export const CreditPreview = ({
    credits = [], cardCredits = [], settings = {}, isExporting = false,
    exportType = 'full', singleCardIndex = -1, onCreditClick, onUpdateCredit,
    onEditingStateChange, isEditable = false, scrollRef, handleLogoDragStart,
    handleLogoDragOver, handleLogoDrop, handleLogoDragEnd, draggedLogoInfo,
    dragOverLogoInfo, activeLogoId, removeLogoFromGrid, onSelectLogo, resolution, activeCreditId,
    deviceScaleFactor = 1, // supersample factor the backend renders at (e.g. 2); always 1 in the live browser preview
    previewPaneWidth = 0 // preview only: width of the on-screen frame (px), used to fit the stage — see PREVIEW_STAGE_WIDTH
}) => {

    // Focus handoff for multi-name cells: after a split/merge commits, the
    // matching cell focuses itself with the caret at `offset`. Ref (not state)
    // — the commit itself triggers the re-render that delivers it.
    const pendingNameFocusRef = React.useRef(null); // { creditId, index, offset }

    // --- Preview stage (WYSIWYG) -------------------------------------------
    // The preview lays credits out on a fixed-width stage with the export's
    // proportions, then zooms the whole stage to fit the pane. Font sizes,
    // spacing and column widths therefore keep the same relationship to the
    // frame as in the export at ANY window size, so wrapping and spacing in the
    // preview are what the render will show. Measured from the scroll view's
    // own parent (handles scrollbars); the card view uses previewPaneWidth.
    const stageMeasureRef = React.useRef(null);
    const [measuredPaneWidth, setMeasuredPaneWidth] = React.useState(0);
    React.useEffect(() => {
        if (isExporting) return undefined;
        const parent = stageMeasureRef.current && stageMeasureRef.current.parentElement;
        if (!parent || typeof ResizeObserver === 'undefined') return undefined;
        const update = () => setMeasuredPaneWidth(parent.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(parent);
        return () => ro.disconnect();
    });

    const renderCredit = (credit, isCardPreview = false) => {
        // --- START: Define Scaling Factor Variables ---
        let scrollExportScalingFactor = 0.73; // Default for scrolling text
        let cardExportScalingFactor = 0.73;   // Default for card text (will be adjusted)
        // --- END: Define Scaling Factor Variables ---

        // All per-credit vs global style decisions come from the shared resolver
        // (also used by the Inspector), so preview, export and the style panel agree.
        const typo = resolveCreditTypography(credit, settings, isCardPreview);

        // --- Adjust Scaling Factors based on Export Resolution ---
        if (isExporting) {
            const [width] = (resolution || settings.resolution || '1920x1080').split('x').map(Number);

            // Set SCROLL scaling
            if (width === 1280) scrollExportScalingFactor = 0.5;
            else if (width === 1920) scrollExportScalingFactor = 0.73; // Base 1080p
            else if (width === 2560) scrollExportScalingFactor = 1.0; // 1440p
            else if (width === 3840) scrollExportScalingFactor = 1.5; // 4K
            else scrollExportScalingFactor = 1.0; // Default

            // --- START: Set CARD scaling (This is the crucial fix) ---
            if (width === 1280) cardExportScalingFactor = 0.5;   // Smaller factor for 720p
            else if (width === 1920) cardExportScalingFactor = 0.73;  // Base factor for 1080p
            else if (width === 2560) cardExportScalingFactor = 1.0;   // Larger factor for 1440p
            else if (width === 3840) cardExportScalingFactor = 1.5;   // Largest factor for 4K
            else cardExportScalingFactor = 1.0; // Default
            // --- END: Set CARD scaling ---
        }
        // --- End Adjust Scaling Factors ---

        // Fixed-unit lengths (px / rem that are NOT derived from a font size)
        // must scale exactly like the fonts do between the preview stage and the
        // export frame: fonts are drawn at size x exportScale x deviceScaleFactor
        // (the renderer pre-multiplies font sizes by the 2x supersample factor),
        // so these get the same factor. Several used to skip the 2x, so letter
        // spacing, column gaps and rem gaps came out at roughly half their
        // preview proportion in the render.
        const scrollUnitScale = isExporting ? scrollExportScalingFactor * (deviceScaleFactor || 1) : 1;
        const cardUnitScale = isExporting ? cardExportScalingFactor * (deviceScaleFactor || 1) : 1;

        // --- COMMON STYLE RESOLUTION ---
        // For scroll credits, prefer scrollFontFamily; for card credits, prefer cardFontFamily
        const { fontFamily, fontWeight, fontStyle } = resolveFontProperties(
            typo.fontFamily.value,
            typo.fontWeight.value,
            typo.fontStyle.value
        );

        // Resolve font size, applying export scaling IF NOT A CARD PREVIEW
        const baseFontSize = typo.fontSize.value;
        // Use scrollExportScalingFactor for non-cards
        const effectiveFontSize = (isExporting && !isCardPreview) ? baseFontSize * scrollExportScalingFactor : baseFontSize;

        const effectiveTextDecoration = typo.textDecoration.value;
        const effectiveLineHeight = typo.lineHeight.value;
        const effectiveLetterSpacing = typo.letterSpacing.value;

        const effectiveBlockNameColor = typo.blockNameColor.value;
        const effectiveRoleColor = typo.roleColor.value;
        const effectiveNameColor = typo.nameColor.value;

        const blockAlignment = credit.blockAlignment || 'center';
        const effectiveTextAlign = typo.textAlign.value;

        const baseMarginBottomMultiplier = (typeof credit.marginBottom !== 'undefined') ? credit.marginBottom : settings.blockSpacing; // This is now a multiplier (e.g., 2). Per-credit override, same as gutterWidth/columnGap/roleSpacing below — not gated behind the style-only `override` flag, since the Bottom Spacing control writes via handleUpdate() and never sets it.
        const previewSpacingMultiplier = 0.80; // Keep preview multiplier for WYSIWYG tuning

        // Determine a reference font size for margin calculation (use the primary text size)
        let referenceFontSizePx = effectiveFontSize; // Default to the general effective size
        if (credit.type === 'dual' || credit.type === 'multiple') {
             const nameFontSizeValue = typo.nameFontSize.value;
             referenceFontSizePx = isExporting ? nameFontSizeValue * scrollExportScalingFactor : nameFontSizeValue;
        }
        // Add other credit types if they have distinct primary font sizes

        // Calculate margin in pixels, relative to the reference font size
        // We use a factor (e.g., 0.8) multiplied by the multiplier to get a pixel value roughly equivalent to the old rem spacing. Adjust 0.8 if needed.
        const calculatedMarginPx = referenceFontSizePx * baseMarginBottomMultiplier * 0.8;

        // Apply scaling only during export, use preview multiplier otherwise
        const effectiveMarginBottomPx = isExporting
            ? calculatedMarginPx // The referenceFontSizePx is already scaled for export
            : referenceFontSizePx * baseMarginBottomMultiplier * previewSpacingMultiplier; // Use preview font size and multiplier

        const effectiveMarginBottom = `${effectiveMarginBottomPx}px`; // Use pixels directly


        // Render one editable text field: ImperativeEditable while editing
        // (React never renders children into it -> no reconciliation against
        // browser-mutated DOM, the source of the duplicate/crash bugs),
        // and the original static markup for read-only/export rendering.
        //
        // `options.parseGuildBrackets` and `options.onCommit` let this serve
        // card text layers too: multiple independent fields on one credit
        // (indexed, not keyed by field name) that render `[bracketed]`
        // segments smaller in the static/export view. That rich span
        // rendering only applies when NOT editing — while editing, this
        // field (like every other converted field) is plain text, since
        // ImperativeEditable owns the DOM directly and never renders JSX
        // children into it. Nothing about the actual export/static output
        // changes; only the live in-editor keystroke-by-keystroke appearance
        // of bracketed text loses its smaller size until you click away.
        const renderTextField = (field, value, transform, style, Tag = 'div', options = {}) => {
            const { key, parseGuildBrackets = false, onCommit, gapEm } = options;
            return isEditable ? (
                <ImperativeEditable
                    key={key}
                    as={Tag}
                    value={value}
                    transform={transform}
                    gapEm={gapEm}
                    className="editable-text-box outline-none"
                    style={style}
                    onEditStart={() => { if (onCreditClick) onCreditClick(credit.id); if (onEditingStateChange) onEditingStateChange(true); }}
                    onEditEnd={() => { if (onEditingStateChange) onEditingStateChange(false); }}
                    onCommit={onCommit || ((text) => { if (onUpdateCredit) onUpdateCredit(credit.id, field, text, null, isCardPreview); })}
                />
            ) : (
                <Tag key={key} style={style}>
                    <EditableCreditText value={value} transform={transform} parseGuildBrackets={parseGuildBrackets} gapEm={gapEm} />
                </Tag>
            );
        };

        // [FAMILY/WEIGHT PAIRING FIX] The dual, multiple and title blocks used to
        // take fontFamily from the resolved dynamicStyle but fontWeight straight
        // from the raw settings value, so the weight was never validated against
        // the family's available variants. That seam let a family and a weight
        // disagree — e.g. family resolved to Futura while the weight passed
        // through as something no bundled Futura face answers, which then fell
        // back to a generic sans in the export. Resolving the pair together keeps
        // them consistent.
        const resolveForWeight = (weight) => resolveFontProperties(
            typo.fontFamily.value,
            weight,
            typo.fontStyle.value
        );

        const dynamicStyle = {
            fontSize: `${effectiveFontSize}px`, // Applies to scroll credits mainly
            fontFamily,
            marginBottom: effectiveMarginBottom,
            fontWeight,
            fontStyle,
            textDecoration: effectiveTextDecoration,
            cursor: 'pointer',
            lineHeight: effectiveLineHeight,
            letterSpacing: `${effectiveLetterSpacing * scrollUnitScale}px`,
            width: (credit.type === 'title' || credit.type === 'single') ? 'auto' : '100%',
            maxWidth: '100%'
        };

        const creditItemProps = {
            id: `preview-credit-${credit.id}`,
            onClick: () => { if (onCreditClick) onCreditClick(credit.id); },
             className: `credit-item transition-colors duration-200 hover:bg-white/5 rounded-md ${
                (credit.type === 'title' || credit.type === 'single') ? 'flex' : ''
            }`,
             style: (credit.type === 'title' || credit.type === 'single')
                ? { justifyContent: blockAlignment, width: '100%' }
                : { width: '100%' }
        };

        switch (credit.type) {
            case 'main-title': // Title Card Rendering
                return (
                    <div
                        key={credit.id}
                        id={`preview-credit-${credit.id}`}
                        onClick={() => { if (onCreditClick) onCreditClick(credit.id); }}
                        // REVERTED (see note below): fit-content measured WORSE on real content —
                        // one specific line (containing an unusual internal whitespace run) drifted
                        // 126px off-center under fit-content, vs ~11-20px under plain auto. Suspected
                        // cause: Chromium's intrinsic/max-content width calc for that line disagrees
                        // with its actual painted width under white-space:pre when the line contains
                        // a run of multiple spaces/tabs — fit-content locks the box to that
                        // miscalculated intrinsic width, so the mismatch becomes visible. auto,
                        // while not rigorous, doesn't pin the box to that calculation the same way
                        // and happened to track the real content better here. Needs a real diagnosis
                        // (confirm what characters make up that gap) before touching this again.
                        // textAlign is applied per-layer below (text justification only, not block position).
                        style={{ width: 'auto', cursor: 'pointer' }}
                        className="credit-item transition-colors duration-200 hover:bg-white/5 rounded-md p-2"
                    >
                        {(credit.textLayers || []).map((layer, index) => {
                            const layerFont = resolveFontProperties(
                                layer.fontFamily || settings.cardFontFamily,
                                layer.fontWeight,
                                layer.fontStyle
                            );
                            const baseLayerFontSize = layer.fontSize || settings.cardFontSize || 48;
                            // --- USE THE CORRECT SCALING FACTOR FOR CARDS ---
                            const effectiveLayerFontSize = isExporting ? baseLayerFontSize * cardExportScalingFactor : baseLayerFontSize;
                            // --- END USE THE CORRECT SCALING FACTOR ---

                            const layerSpacing = credit.cardVerticalSpacing ?? 0.5;
                            const marginBottomStyle = (index < (credit.textLayers || []).length - 1)
                                ? `${layerSpacing * cardUnitScale}rem`
                                : '0';

                            return renderTextField('textLayers', layer.content, layer.transform, {
                                fontSize: `${effectiveLayerFontSize}px`, // Apply corrected size
                                fontWeight: layerFont.fontWeight,
                                fontStyle: layerFont.fontStyle,
                                color: layer.color || '#ffffff',
                                fontFamily: layerFont.fontFamily,
                                whiteSpace: 'pre', // [WRAP FIX] card text breaks only where the user breaks it
                                textTransform: layer.transform || 'normal',
                                lineHeight: 1.2,
                                marginBottom: marginBottomStyle,
                                // textAlign here controls text JUSTIFICATION only (left/center/right within the text block).
                                // It does NOT shift the block position — that is controlled by cardPositionX.
                                textAlign: credit.cardTextAlign || 'center',
                            }, 'div', {
                                key: layer.id,
                                parseGuildBrackets: true,
                                // Per-card Name Gap (Inspector > Layout). em of this
                                // layer's font size, so it scales with the text at
                                // every export resolution. undefined = natural spaces.
                                gapEm: credit.cardNameGap,
                                onCommit: (text) => { if (onUpdateCredit) onUpdateCredit(credit.id, 'textLayers', text, index, true); },
                            });
                        })}
                    </div>
                );

            case 'title':
                const headerFontSize = typo.headerFontSize.value;
                const headerFontWeight = typo.headerFontWeight.value;
                const headerResolved = resolveForWeight(headerFontWeight);
                // Use scrollExportScalingFactor for headers
                const effectiveHeaderFontSize = isExporting ? headerFontSize * scrollExportScalingFactor : headerFontSize;

                // Gap below a header scales with the size the header is actually
                // DRAWN at (its own size if it has one, else the global Header
                // size). It used to use the base text size unless the header had
                // its own size — so the same Bottom Spacing value gave different
                // gaps depending on whether a size override existed. Multi-name
                // headers use the same rule (see 'multiple'), so they match.
                const headerMarginPx = effectiveHeaderFontSize * baseMarginBottomMultiplier * previewSpacingMultiplier;

                return (
                    <div key={credit.id} {...creditItemProps}>
                        {renderTextField('name', credit.name, settings.blockNameCaps, {
                            ...dynamicStyle,
                            marginBottom: `${headerMarginPx}px`,
                            color: effectiveBlockNameColor,
                            fontSize: `${effectiveHeaderFontSize}px`, // Apply correct scaled size
                            fontFamily: headerResolved.fontFamily,
                            fontWeight: headerResolved.fontWeight,
                            fontStyle: headerResolved.fontStyle,
                            textAlign: effectiveTextAlign,
                            whiteSpace: 'pre', // [WRAP FIX] explicit breaks only — no forced wrapping at export width
                            width: 'auto'
                        })}
                    </div>
                );

            case 'dual': { // Added curly braces for block scope
                // [FIX] Was `(credit.gutterWidth ?? settings.gutterWidth) || 3` — the
                // trailing `|| 3` treats a legitimate 0 as falsy and silently
                // replaces it with 3, so a user-set zero gap (correctly tight in
                // preview, since preview reads this same live value) got
                // overridden. `??` alone only falls through on null/undefined,
                // so 0 is respected everywhere in this chain.
                const baseGutterWidthMultiplier = credit.gutterWidth ?? settings.gutterWidth ?? 3; // Treat gutterWidth as a multiplier (e.g., 3); per-credit override falls back to global
                const previewGutterMultiplier = 1.0; // Adjust slightly for preview if needed, 1.0 matches export closer usually

                // Use the already calculated effectiveFontSize (which includes export scaling) as the reference
                const referenceFontSizePx = effectiveFontSize;

                // Calculate gutter in pixels relative to the font size
                // We use a factor (e.g., 0.8 or 1.0) multiplied by the multiplier. Adjust as needed.
                // Start with 1.0 since gutter is usually wider than vertical space.
                const calculatedGutterPx = referenceFontSizePx * baseGutterWidthMultiplier * 1.0;

                // Apply scaling for export vs. preview (referenceFontSizePx already scaled for export)
                const effectiveGutterPx = isExporting
                    ? calculatedGutterPx // Use already scaled reference size
                    : referenceFontSizePx * baseGutterWidthMultiplier * previewGutterMultiplier; // Use preview ref size * preview multiplier

                const effectiveGutterWidth = `${effectiveGutterPx}px`; // Use pixels

                // Resolve per-block alignment mode:
                // - left/center/right: both columns align the same way
                // - justified: role flush to the block's left edge, name flush to the right edge (dots-in-the-middle spread)
                // - classic: role hugs the gutter from the right, name hugs the gutter from the left
                //   (the traditional "Role .... Name" screen-credit look, converging at the gutter)
                const dualAlignment = credit.dualAlignment || settings.dualAlignment || 'classic';
                const roleJustifySelf = dualAlignment === 'justified' ? 'start' : dualAlignment === 'classic' ? 'end' : (dualAlignment === 'center' ? 'center' : (dualAlignment === 'right' ? 'end' : 'start'));
                const nameJustifySelf = dualAlignment === 'justified' ? 'end' : dualAlignment === 'classic' ? 'start' : (dualAlignment === 'center' ? 'center' : (dualAlignment === 'right' ? 'end' : 'start'));
                const roleTextAlignValue = dualAlignment === 'justified' ? 'left' : dualAlignment === 'classic' ? 'right' : dualAlignment;
                const nameTextAlignValue = dualAlignment === 'justified' ? 'right' : dualAlignment === 'classic' ? 'left' : dualAlignment;

                const roleFontSizeValue = typo.roleFontSize.value;
                const roleFontWeightValue = typo.roleFontWeight.value;
                // Use scrollExportScalingFactor for role size
                const effectiveRoleFontSize = isExporting ? roleFontSizeValue * scrollExportScalingFactor : roleFontSizeValue;
                const roleTransformValue = typo.roleTransform.value;

                const nameFontSizeValue = typo.nameFontSize.value;
                const nameFontWeightValue = typo.nameFontWeight.value;
                 // Use scrollExportScalingFactor for name size
                const effectiveNameFontSize = isExporting ? nameFontSizeValue * scrollExportScalingFactor : nameFontSizeValue;
                const nameTransformValue = typo.nameTransform.value;

                const roleResolved = resolveForWeight(roleFontWeightValue);
                const nameResolved = resolveForWeight(nameFontWeightValue);

                return (
                    <div key={credit.id} {...creditItemProps} style={{...creditItemProps.style, ...dynamicStyle}} >
                        <div style={{
                            display: 'grid',
                            // [WRAP FIX] minmax(0, 1fr) keeps both tracks locked at equal width;
                            // with white-space: pre a long name overflows its own track visibly
                            // instead of widening it and shifting the role/name gutter off-center.
                            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                            columnGap: effectiveGutterWidth,
                            alignItems: 'baseline',
                            width: '100%'
                         }}>
                            {renderTextField('role', credit.role, roleTransformValue, {
                                color: effectiveRoleColor,
                                textAlign: roleTextAlignValue,
                                justifySelf: roleJustifySelf,
                                whiteSpace: 'pre', // [WRAP FIX] explicit breaks only
                                fontSize: `${effectiveRoleFontSize}px`, // Apply correct scaled size
                                fontWeight: roleResolved.fontWeight,
                                fontFamily: roleResolved.fontFamily,
                                fontStyle: roleResolved.fontStyle,
                                textDecoration: dynamicStyle.textDecoration,
                                lineHeight: dynamicStyle.lineHeight,
                                letterSpacing: dynamicStyle.letterSpacing,
                            }, 'span')}

                            {renderTextField('name', credit.name, nameTransformValue, {
                                color: effectiveNameColor,
                                textAlign: nameTextAlignValue,
                                justifySelf: nameJustifySelf,
                                whiteSpace: 'pre', // [WRAP FIX] explicit breaks only
                                fontSize: `${effectiveNameFontSize}px`, // Apply correct scaled size
                                fontWeight: nameResolved.fontWeight,
                                fontFamily: nameResolved.fontFamily,
                                fontStyle: nameResolved.fontStyle,
                                textDecoration: dynamicStyle.textDecoration,
                                lineHeight: dynamicStyle.lineHeight,
                                letterSpacing: dynamicStyle.letterSpacing,
                            }, 'span')}
                        </div>
                    </div>
                );
            }

           case 'multiple': { // Multi-Name Block Rendering
                // Song Credits (credit.multipleMode === 'song') are capped at 3
                // songs per block by the Song Board, so this block only ever
                // needs at most 3 columns — derived from the actual song count,
                // not the manual "Columns" setting (which still applies to
                // ordinary Multi-Name Blocks). Fewer than 3 songs narrows the
                // grid itself and centers it, so e.g. a single song sits
                // centered in the middle rather than stretched full-width or
                // pinned to the left column — same idea as a single logo
                // centering itself in a Logo Board row. If a block somehow has
                // more than 3 saved songs (e.g. older data from before this
                // cap existed), nothing is dropped — it just falls back to
                // wrapping onto additional rows, same as before.
                const isSongMode = credit.multipleMode === 'song';
                const songEntries = isSongMode ? (credit.name ? parseSongEntries(credit.name) : [{ title: '', info: '' }]) : null;
                const songActiveColumns = isSongMode ? Math.min(3, Math.max(1, songEntries.length)) : null;

                // Resolve role-specific styles and apply SCROLL scaling
                const roleFontSizeValue = typo.roleFontSize.value;
                const roleFontWeightValue = typo.roleFontWeight.value;
                const effectiveRoleFontSize = isExporting ? roleFontSizeValue * scrollExportScalingFactor : roleFontSizeValue;
                const roleTransformValue = typo.roleTransform.value;

                 // Resolve name-specific styles and apply SCROLL scaling
                const nameFontSizeValue = typo.nameFontSize.value;
                const nameFontWeightValue = typo.nameFontWeight.value;
                const effectiveNameFontSize = isExporting ? nameFontSizeValue * scrollExportScalingFactor : nameFontSizeValue;
                const nameTransformValue = typo.nameTransform.value;

                const roleResolved = resolveForWeight(roleFontWeightValue);
                const nameResolved = resolveForWeight(nameFontWeightValue);

                // Resolve column count and gap, applying SCROLL scaling to gap
                const columnCount = credit.multipleNamesColumnCount || 1;
                const effectiveColumnGap = (credit.columnGap ?? 2) * previewSpacingMultiplier * scrollUnitScale;

                // Role-to-names spacing (rem): a per-credit multiplier over the
                // original 0.5rem base. Unlike Column Gap, the old marginBottom
                // here was a flat 0.5rem with NO preview/export scaling, so this
                // stays unscaled too — the default (multiplier 1) renders
                // identically to before in both preview and export.
                // Header -> names gap. Default matches every other header's gap
                // (same formula a 'title' credit uses for its bottom margin, so
                // preview/export scale identically); per-block "Header Spacing"
                // adjusts only this gap. See resolveMultiHeaderSpacing.
                const headerSpacing = resolveMultiHeaderSpacing(credit, settings);
                // Same rule as a 'title' header: scale with this header's drawn size.
                const effectiveRoleSpacingCss = headerSpacing.mode === 'legacy'
                    ? `${headerSpacing.rem * scrollUnitScale}rem`
                    : `${(effectiveRoleFontSize || 16) * headerSpacing.multiplier * previewSpacingMultiplier}px`;

                // [EXPORT WIDTH COMPENSATION — STOPGAP] mirrors what logo
                // blocks already do via blockWidthAdjustment (see table at
                // top of file). Scroll export only — cards use their own
                // scaling and are left untouched.
                let blockWidthAdjustment = 1.0;
                if (isExporting && !isCardPreview) {
                    const [exportW] = (resolution || settings.resolution || '1920x1080').split('x').map(Number);
                    blockWidthAdjustment = MULTI_BLOCK_EXPORT_WIDTH_ADJUSTMENT[exportW] ?? 1.0;
                }
                const effectiveBlockWidth = (credit.blockWidth || 100) * blockWidthAdjustment;

                // Role text styles
                const roleStyle = {
                    ...dynamicStyle, // Inherit base styles (excluding margin-bottom)
                    fontSize: `${effectiveRoleFontSize}px`, // Apply scaled size
                    fontWeight: roleResolved.fontWeight,
                    fontFamily: roleResolved.fontFamily,
                    fontStyle: roleResolved.fontStyle,
                    color: effectiveRoleColor,
                    textAlign: effectiveTextAlign,
                    marginBottom: effectiveRoleSpacingCss, // Header -> names gap (matches other headers by default)
                    whiteSpace: 'pre' // [WRAP FIX] explicit breaks only — this field was missed in the earlier pass
                };

                return (
                    // Outer container for the entire block, applies margin-bottom and block width
                    <div key={credit.id} {...creditItemProps} style={{ ...dynamicStyle, textAlign: effectiveTextAlign, width: `${effectiveBlockWidth}%` }}>
                        {/* Render Role if it exists */}
                        {credit.role && (
                            <div style={roleStyle}>
                                {renderTextField('role', credit.role, roleTransformValue, undefined)}
                            </div>
                        )}

                        {/* Grid container for names.
                            [COLUMN FIX] The grid itself is no longer contentEditable.
                            Each name is its own independently editable NameCell, so
                            editing one name cannot disturb the DOM of its neighbors
                            and React never fights the browser inside a focused cell. */}
                         <div
                            style={{
                                display: 'grid',
                                // [WRAP FIX] minmax(0, 1fr), not plain 1fr: locks every column
                                // to an equal, fixed share regardless of content. Plain 1fr is
                                // minmax(auto, 1fr), so with white-space: pre (no auto-wrap) a
                                // single long unbroken line grows its own track past its fair
                                // share, widening the whole grid beyond its container — visible
                                // in export (real pixel widths) far more often than in-editor.
                                // Same fix already applied to the 'dual' grid above.
                                gridTemplateColumns: `repeat(${isSongMode ? songActiveColumns : columnCount}, minmax(0, 1fr))`,
                                gap: `${effectiveColumnGap}rem`, // Scaled gap
                                // Apply common font styles directly to the grid container
                                color: effectiveNameColor,
                                fontSize: `${effectiveNameFontSize}px`, // Apply scaled size
                                fontWeight: nameResolved.fontWeight,
                                fontFamily: nameResolved.fontFamily,
                                fontStyle: nameResolved.fontStyle,
                                textDecoration: dynamicStyle.textDecoration,
                                lineHeight: dynamicStyle.lineHeight,
                                letterSpacing: dynamicStyle.letterSpacing,
                                textAlign: effectiveTextAlign,
                                // [BLEED FIX] pre-wrap, not pre: still renders manual breaks
                                // (EditableCreditText's <br/>) exactly like pre did, but ALSO
                                // wraps a line onto an extra line when it's wider than its
                                // minmax(0, 1fr) track. Plain 'pre' never wraps at all, so once
                                // that minmax(0, 1fr) cap (above) stopped columns from growing
                                // to fit long content, an oversized line had nowhere to go but
                                // sideways — bleeding straight through the gap into the next
                                // column. pre-wrap lets it drop to a second line inside its own
                                // column instead, the same way a manually-typed second line
                                // already does.
                                whiteSpace: 'pre-wrap',
                                // Song Credits only: narrow the grid itself to match how many
                                // of the (up to 3) song columns are actually in use, and center
                                // it — so 1 song occupies ~1/3 width centered, 2 songs ~2/3
                                // centered, and 3 songs fill the full width as before.
                                ...(isSongMode ? { width: `${(songActiveColumns / 3) * 100}%`, margin: '0 auto' } : {}),
                            }}
                         >
                            {/* SMART SPLIT FIX: each name is its own grid item so CSS grid
                                auto-flow actually distributes names across `columnCount`
                                columns. Previously the whole name string was rendered as a
                                single div spanning all columns, so there was never more than
                                one item for the grid to place — changing the column count
                                only resized empty tracks instead of redistributing names. */}
                            {(() => {
                                // --- Song Credits mode -------------------------------------
                                // Each grid item is a Title + Info pair instead of a single
                                // name. Editing happens exclusively in the Song Board (App.js
                                // openSongBoard/commitSongBoard) — same convention as Logo
                                // blocks, which are edited in the Logo Board rather than
                                // inline — so this always renders the static read form,
                                // regardless of isEditable. Title always takes its color/
                                // family/caps from the "Name Typography" style slot and Info
                                // always takes its from "Role Typography" — that mapping never
                                // changes, so those Style-tab sections always mean the same
                                // thing. `songSwapBold` only swaps which slot's WEIGHT each
                                // line uses, so the "Bold" toggle affects boldness alone.
                                if (credit.multipleMode === 'song') {
                                    const swapBold = !!credit.songSwapBold;
                                    // Title is ALWAYS styled with the Name slot's color/family/
                                    // caps, and Info is ALWAYS styled with the Role slot's — so
                                    // "Name Typography" in the Style tab always means Song Title
                                    // and "Role Typography" always means Credit Info, with no
                                    // hidden remapping. songSwapBold only decides which of the
                                    // two slots' WEIGHT each line borrows, so the toggle affects
                                    // boldness alone and never silently changes color or case.
                                    const titleSlot = {
                                        color: effectiveNameColor,
                                        fontWeight: swapBold ? roleResolved.fontWeight : nameResolved.fontWeight,
                                        fontFamily: nameResolved.fontFamily,
                                        fontStyle: nameResolved.fontStyle,
                                        transform: nameTransformValue,
                                    };
                                    const infoSlot = {
                                        color: effectiveRoleColor,
                                        fontWeight: swapBold ? nameResolved.fontWeight : roleResolved.fontWeight,
                                        fontFamily: roleResolved.fontFamily,
                                        fontStyle: roleResolved.fontStyle,
                                        transform: roleTransformValue,
                                    };
                                    const songEntrySpacing = (credit.songEntrySpacing ?? 1) * 0.35 * scrollUnitScale;
                                    const entries = songEntries;

                                    return entries.map((entry, i) => (
                                        <div key={i} style={{ marginBottom: i < entries.length - 1 ? `${songEntrySpacing}rem` : 0 }}>
                                            {entry.title && (
                                                <div style={{
                                                    color: titleSlot.color, fontWeight: titleSlot.fontWeight,
                                                    fontFamily: titleSlot.fontFamily, fontStyle: titleSlot.fontStyle,
                                                    whiteSpace: 'pre-wrap',
                                                }}>
                                                    <EditableCreditText value={entry.title} transform={titleSlot.transform} />
                                                </div>
                                            )}
                                            {entry.info && (
                                                <div style={{
                                                    color: infoSlot.color, fontWeight: infoSlot.fontWeight,
                                                    fontFamily: infoSlot.fontFamily, fontStyle: infoSlot.fontStyle,
                                                    whiteSpace: 'pre-wrap', marginTop: '0.15em',
                                                }}>
                                                    <EditableCreditText value={entry.info} transform={infoSlot.transform} />
                                                </div>
                                            )}
                                        </div>
                                    ));
                                }

                                const namesList = credit.name ? credit.name.split('\n') : [''];

                                if (!isEditable) {
                                    // Static / export rendering — unchanged: soft breaks
                                    // render as <br/> via EditableCreditText.
                                    return namesList.map((line, i) => (
                                        <div key={i}>
                                            <EditableCreditText value={line} transform={nameTransformValue} />
                                        </div>
                                    ));
                                }

                                // --- Editing model for name cells ---
                                // Stored:  '\n' separates ENTRIES, U+2028 = line WITHIN an entry.
                                // In the DOM while editing, a cell shows its entry with soft
                                // breaks as real '\n' (U+2028 does not visually break in
                                // Chrome), so:  display '\n'  <->  stored U+2028.
                                const toDisplay = (entry) => (entry || '').replace(/\u2028/g, '\n');
                                const toStored = (text) => (text || '').replace(/\r/g, '').replace(/\n+$/, '').replace(/\n/g, SOFT_BREAK);

                                const commitEntries = (index, removeCount, replacementEntries, focusIndex, focusOffset) => {
                                    if (!onUpdateCredit) return;
                                    const next = [...namesList];
                                    next.splice(index, removeCount, ...replacementEntries);
                                    pendingNameFocusRef.current = (focusIndex !== undefined)
                                        ? { creditId: credit.id, index: focusIndex, offset: focusOffset || 0 }
                                        : null;
                                    onUpdateCredit(credit.id, 'name', next.join('\n'), null, isCardPreview);
                                };

                                const pendingFocus = pendingNameFocusRef.current;

                                return namesList.map((line, i) => (
                                    <ImperativeEditable
                                        key={i}
                                        value={line}
                                        displayValue={toDisplay(applyTextTransformStatic(line, nameTransformValue))}
                                        encodeSoftBreaksOnCopy
                                        className="editable-text-box outline-none"
                                        style={{ minHeight: '1em' }} // keeps an emptied cell clickable
                                        shouldFocus={!!(pendingFocus && pendingFocus.creditId === credit.id && pendingFocus.index === i)}
                                        focusOffset={pendingFocus ? pendingFocus.offset : 0}
                                        onFocusHandled={() => { pendingNameFocusRef.current = null; }}
                                        onEditStart={() => {
                                            if (onCreditClick) onCreditClick(credit.id);
                                            if (onEditingStateChange) onEditingStateChange(true);
                                        }}
                                        onEditEnd={() => { if (onEditingStateChange) onEditingStateChange(false); }}
                                        onCommit={(text) => commitEntries(i, 1, [toStored(text)])}
                                        onSpecialKey={(e, api) => {
                                            // Enter (no shift): split this entry into two at the
                                            // caret, focus lands at the start of the new entry.
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const split = api.getSplit();
                                                if (!split) return true;
                                                api.suppressNextCommit();
                                                commitEntries(i, 1, [toStored(split.before), toStored(split.after)], i + 1, 0);
                                                return true;
                                            }
                                            // Shift+Enter falls through to the default handler:
                                            // inserts a literal '\n' = a visible second line that
                                            // commits as a soft break inside the SAME entry.

                                            // Backspace at the very start: merge into the previous
                                            // entry (deleting an empty cell collapses it away).
                                            if (e.key === 'Backspace' && api.isCollapsedAtStart()) {
                                                if (i > 0) {
                                                    e.preventDefault();
                                                    const current = api.getText();
                                                    const prevDisplay = toDisplay(applyTextTransformStatic(namesList[i - 1], nameTransformValue));
                                                    api.suppressNextCommit();
                                                    commitEntries(i - 1, 2, [toStored(prevDisplay + current)], i - 1, prevDisplay.length);
                                                    return true;
                                                }
                                                // First cell, empty, with entries after it: remove it.
                                                if (i === 0 && namesList.length > 1 && api.getText() === '') {
                                                    e.preventDefault();
                                                    api.suppressNextCommit();
                                                    commitEntries(0, 1, [], 0, 0);
                                                    return true;
                                                }
                                            }
                                            // Delete at the very end: pull the next entry up into this one.
                                            if (e.key === 'Delete' && api.isCollapsedAtEnd() && i < namesList.length - 1) {
                                                e.preventDefault();
                                                const current = api.getText();
                                                const nextDisplay = toDisplay(applyTextTransformStatic(namesList[i + 1], nameTransformValue));
                                                api.suppressNextCommit();
                                                commitEntries(i, 2, [toStored(current + nextDisplay)], i, current.length);
                                                return true;
                                            }
                                            return false;
                                        }}
                                        onPasteText={(text, api) => {
                                            // Pasting a multi-line list creates one entry per line
                                            // (matching how names are stored), spliced in at the caret.
                                            // A REAL newline is what signals "separate entries" here —
                                            // e.g. a list copied from an external doc/spreadsheet.
                                            const clean = (text || '').replace(/\r/g, '');
                                            if (clean.includes('\n')) {
                                                const split = api.getSplit();
                                                if (!split) return false;
                                                const lines = clean.split('\n');
                                                const entries = [
                                                    toStored(split.before + lines[0]),
                                                    ...lines.slice(1, -1).map(toStored),
                                                    toStored(lines[lines.length - 1] + split.after),
                                                ];
                                                api.suppressNextCommit();
                                                commitEntries(i, 1, entries, i + lines.length - 1, lines[lines.length - 1].length);
                                                return true;
                                            }
                                            // No real newline, but a SOFT_BREAK marker means this text
                                            // was copied from a single entry elsewhere in this grid
                                            // (see encodeSoftBreaksOnCopy) — it belongs in ONE entry.
                                            // Insert its display form (soft break -> literal '\n', same
                                            // as typing Shift+Enter) so it renders immediately and
                                            // commits back to a soft break on blur.
                                            if (clean.includes(SOFT_BREAK)) {
                                                api.insert(clean.split(SOFT_BREAK).join('\n'));
                                                return true;
                                            }
                                            return false; // plain single-line text: default inline insert
                                        }}
                                    />
                                ));
                            })()}
                        </div>
                    </div>
                );
            }

            case 'single':
                 // Use scrollExportScalingFactor for single line text size
                 const effectiveSingleFontSize = isExporting ? baseFontSize * scrollExportScalingFactor : baseFontSize;
                 return (
                    <div key={credit.id} {...creditItemProps}>
                        {renderTextField('name', credit.name, settings.nameCaps, {
                            ...dynamicStyle,
                            color: effectiveNameColor,
                            fontSize: `${effectiveSingleFontSize}px`, // Apply correct scaled size
                            textAlign: effectiveTextAlign,
                            whiteSpace: 'pre', // [WRAP FIX] explicit breaks only — no forced wrapping at export width
                            width: 'auto'
                        })}
                    </div>
                );

            // --- Logo rendering remains unchanged, assuming previous scaling logic was correct ---
            case 'logo': {
                 // Logo scaling logic remains the same as before
                const [width] = (resolution || settings.resolution || '1920x1080').split('x').map(Number);
                let logoScalingFactor = 1.0, gapScalingFactor = 1.0, blockWidthAdjustment = 1.0, cellPaddingFactor = 1.0;

                if (isExporting) {
                    if (width === 1280) { logoScalingFactor = 0.985; gapScalingFactor = 1.05; blockWidthAdjustment = 1.0; cellPaddingFactor = 1.1; }
                     else if (width === 1920) { logoScalingFactor = .98; gapScalingFactor = 1.13; blockWidthAdjustment = 1.0; cellPaddingFactor = 1.1; }
                     else if (width === 2560) { logoScalingFactor = 1.0; gapScalingFactor = 1.13; blockWidthAdjustment = 1.0; cellPaddingFactor = 1.1; }
                     else if (width === 3840) { logoScalingFactor = 1.0; gapScalingFactor = 1.13; blockWidthAdjustment = 1.0; cellPaddingFactor = 1.1; }
                }

                const logoBaseStyle = {
                    ...dynamicStyle, // Includes margin bottom etc.
                    display: 'flex',
                    justifyContent: credit.logoAlignment || 'center',
                    alignItems: 'center',
                    padding: '0.5rem 0',
                    width: '100%'
                };

                if (credit.layout === 'rows' && Array.isArray(credit.rows)) {
                    const baseGridGapPx = (credit.gridGap || 1) * 16;
                    const effectiveRowSpacingPx = baseGridGapPx * gapScalingFactor; // vertical space between rows
                    const effectiveBlockWidth = (credit.blockWidth || 90) * blockWidthAdjustment;
                    // Logos are sized by a consistent HEIGHT reference (like a real logo lockup),
                    // with natural width — not stretched into equal-width boxes. That's what makes
                    // the gap between them predictable and "balanceable" instead of arbitrary.
                    const baseHeightMap = { xs: 32, small: 52, medium: 76, large: 104, xl: 136 };
                    const blockScale = (credit.logoScale ?? 100) / 100; // one overall size dial for the whole board

                    return (
                        <div key={credit.id} {...creditItemProps} style={logoBaseStyle}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: `${effectiveRowSpacingPx}px`,
                                    width: `${effectiveBlockWidth}%`,
                                    margin: '0 auto',
                                }}
                            >
                                {credit.rows.map((row) => {
                                    // Per-row gap override, falling back to the credit-level default.
                                    const rowGapPx = ((row.gap ?? credit.gridGap ?? 1) * 16) * gapScalingFactor;
                                    return (
                                    <div
                                        key={row.id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: `${rowGapPx}px`,
                                        }}
                                    >
                                        {(row.logos || []).map((logo, idx) => {
                                            const logoSizeKey = logo.size || 'medium';
                                            // [LOGO SCALE FIX] Every other logo layout (grid, default/single)
                                            // sizes logos with ONLY logoScalingFactor — a resolution-tuned
                                            // constant, same mechanism the fonts use. This branch additionally
                                            // multiplied by raw deviceScaleFactor (2 during export), but nothing
                                            // about this logo's container scales with deviceScaleFactor to
                                            // compensate — the export container stays at the fixed export
                                            // width regardless. That left every 'rows' logo rendering exactly
                                            // 2x too large in export only, blowing past the safe area, while
                                            // preview (where deviceScaleFactor is always 1) looked correct.
                                            // [LOGO EXPORT SIZE] Calibration constant: rows-layout logos read
                                            // 50% too small at 1080p export relative to the editor preview.
                                            // Export-only bump, tuned by eye — preview is untouched.
                                            // [LOGO EXPORT SIZE] logoScalingFactor (above) barely moves across
                                            // resolutions (0.985 -> 1.0, ~2% total) while frame width triples
                                            // (1280 -> 3840) — it's a fine-tuning nudge, not a resolution scale.
                                            // Unlike the grid/default logo layouts (which multiply by `width`
                                            // or use a % container and so scale automatically), this branch
                                            // set a flat px height, so logos held their absolute size instead
                                            // of their size RELATIVE TO THE FRAME as resolution changed.
                                            // resolutionScaleFactor anchors to 1920 (where exportLogoBoost was
                                            // tuned and confirmed correct) and scales linearly with frame width,
                                            // so a logo takes up the same % of frame at every export resolution.
                                            const exportLogoBoost = 1.7;
                                            const resolutionScaleFactor = isExporting ? (width / 1920) : 1;
                                            const effectiveHeightPx = (baseHeightMap[logoSizeKey] ?? baseHeightMap.medium) * blockScale * logoScalingFactor * (isExporting ? exportLogoBoost * resolutionScaleFactor : 1);
                                            return (
                                                <div
                                                    key={logo.id || idx}
                                                    style={{
                                                        flex: '0 0 auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {logo.logoUrl ? (
                                                        <img
                                                            src={logo.logoUrl} alt=""
                                                            style={{
                                                                height: `${effectiveHeightPx}px`,
                                                                width: 'auto',
                                                                maxWidth: `${effectiveHeightPx * 3.5}px`,
                                                                objectFit: 'contain',
                                                                imageRendering: isExporting ? 'auto' : 'crisp-edges'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="bg-zinc-800 border-2 border-dashed border-zinc-600 rounded-md"
                                                            style={{ width: `${effectiveHeightPx * 1.6}px`, height: `${effectiveHeightPx}px` }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                }

                if (credit.layout === 'grid') {
                    const baseGridGapPx = (credit.gridGap || 1) * 16;
                    const effectiveGridGapPx = baseGridGapPx * gapScalingFactor;
                    const effectiveBlockWidth = (credit.blockWidth || 90) * blockWidthAdjustment;
                    const baseCellPaddingPx = 0.5 * 16;
                    const effectiveCellPaddingPx = baseCellPaddingPx * cellPaddingFactor;

                    return (
                        <div key={credit.id} {...creditItemProps} style={logoBaseStyle}>
                            <div
                                onDrop={(e) => isEditable && handleLogoDrop(e, credit.id)}
                                onDragOver={(e) => isEditable && e.preventDefault()}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${credit.gridColumns || 3}, 1fr)`,
                                    gap: `${effectiveGridGapPx}px`,
                                    width: `${effectiveBlockWidth}%`,
                                    margin: '0 auto',
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                }}
                            >
                                {(credit.logos || []).map((logo, index) => {
                                    const isDragging = draggedLogoInfo?.logoId === logo.id;
                                    const isDragOver = dragOverLogoInfo?.logoId === logo.id;
                                    const isSelected = activeLogoId === logo.id;

                                    if (logo.isEmpty) {
                                         return (
                                            <div
                                                key={logo.id || index}
                                                draggable={isEditable}
                                                onDragStart={(e) => isEditable && handleLogoDragStart(e, credit.id, logo.id)}
                                                onDragOver={(e) => isEditable && handleLogoDragOver(e, credit.id, logo.id)}
                                                onDragEnd={isEditable ? handleLogoDragEnd : undefined}
                                                className={`relative group flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer ${isDragging ? 'opacity-30' : ''} ${isDragOver ? 'bg-zinc-700' : ''}`}
                                                style={{ minHeight: `${60 * cellPaddingFactor}px` }}
                                            >
                                                {isEditable && activeCreditId === credit.id && (
                                                    <div className="w-full h-full border-2 border-dashed border-zinc-700 group-hover:border-zinc-600 rounded-md" />
                                                )}
                                                {isEditable && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); removeLogoFromGrid(credit.id, logo.id); }}
                                                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                        title="Remove Spacer"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }

                                    const logoSizeKey = logo.size || credit.logoSize || 'medium';
                                    const logoOpacityValue = logo.opacity ?? credit.logoOpacity ?? 100;
                                    const baseSizeMap = { small: 0.7, medium: 0.85, large: 1.0 };
                                    const effectiveLogoSize = baseSizeMap[logoSizeKey] * logoScalingFactor;
                                    const effectiveOffsetX = (logo.offsetX || 0) * (isExporting ? 2.2 : 1);
                                    const effectiveOffsetY = (logo.offsetY || 0) * (isExporting ? 2.2 : 1);

                                    return (
                                        <div
                                            key={logo.id || index}
                                            draggable={isEditable}
                                            onClick={(e) => { e.stopPropagation(); if (onCreditClick) onCreditClick(credit.id); if (onSelectLogo) onSelectLogo(logo.id); }}
                                            onDragStart={(e) => isEditable && handleLogoDragStart(e, credit.id, logo.id)}
                                            onDragOver={(e) => isEditable && handleLogoDragOver(e, credit.id, logo.id)}
                                            onDragEnd={isEditable ? handleLogoDragEnd : undefined}
                                            className={`relative flex items-center justify-center rounded-lg transition-all duration-200 group cursor-pointer ${isDragging ? 'opacity-30' : ''} ${isDragOver ? 'bg-zinc-700' : ''} ${isSelected ? 'ring-2 ring-purple-500' : ''}`}
                                            style={{
                                                padding: `${effectiveCellPaddingPx}px`,
                                                minHeight: `${60 * cellPaddingFactor}px`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}
                                        >
                                            {logo.logoUrl ? (
                                                <img
                                                    src={logo.logoUrl} alt=""
                                                    style={{
                                                        maxWidth: `${effectiveLogoSize * 100}%`,
                                                        maxHeight: `${effectiveLogoSize * 100}%`,
                                                        objectFit: 'contain',
                                                        opacity: logoOpacityValue / 100,
                                                        pointerEvents: 'none',
                                                        transform: `translate(${effectiveOffsetX}px, ${effectiveOffsetY}px)`,
                                                        imageRendering: isExporting ? 'auto' : 'crisp-edges'
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="bg-zinc-800 border-2 border-dashed border-zinc-600 rounded-md"
                                                    style={{ width: `${effectiveLogoSize * 80}px`, height: `${effectiveLogoSize * 50}px` }}
                                                />
                                            )}
                                            {isEditable && logo.logoUrl && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); removeLogoFromGrid(credit.id, logo.id); }}
                                                    className="absolute -top-1 -right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                                    title="Remove Logo"
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                }

                 const imageStyle = {
                    objectFit: 'contain',
                    opacity: (credit.logoOpacity || 100) / 100,
                    imageRendering: isExporting ? 'auto' : 'crisp-edges',
                    maxWidth: '100%',
                    maxHeight: '100%',
                 };

                 if (credit.layout === 'full-width') {
                    imageStyle.width = '90%';
                     imageStyle.height = 'auto';
                 } else {
                    const logoSizeKey = credit.logoSize || 'medium';
                    if (isExporting) {
                        const gridColumns = 3;
                        const blockWidth = 0.9;
                         const cellWidth = (width * blockWidthAdjustment * blockWidth) / gridColumns;
                        const baseSizeMap = { small: 0.7, medium: 0.85, large: 1.0 };
                         const logoSizeRatio = baseSizeMap[logoSizeKey];
                         const effectiveLogoSize = logoSizeRatio * logoScalingFactor;
                         const calculatedSize = cellWidth * effectiveLogoSize;
                         imageStyle.maxWidth = `${calculatedSize}px`;
                         imageStyle.maxHeight = `${calculatedSize}px`;
                         imageStyle.width = 'auto';
                         imageStyle.height = 'auto';
                    } else {
                        const sizeMap = { small: 280, medium: 350, large: 400 };
                         const baseSize = sizeMap[logoSizeKey];
                         imageStyle.maxWidth = `${baseSize}px`;
                         imageStyle.maxHeight = `${baseSize * 0.6}px`;
                         imageStyle.width = 'auto';
                         imageStyle.height = 'auto';
                    }
                 }

                return (
                    <div key={credit.id} {...creditItemProps} style={logoBaseStyle}>
                        {credit.logoUrl ? (
                            <img src={credit.logoUrl} alt="" style={imageStyle} />
                        ) : (
                            <div className="bg-zinc-800 border-2 border-dashed border-zinc-600 rounded-md" style={{ width: '300px', height: '150px' }} />
                        )}
                    </div>
                );
            }


            default:
                // Use scrollExportScalingFactor for default/single line text size
                 const effectiveDefaultFontSize = isExporting ? baseFontSize * scrollExportScalingFactor : baseFontSize;
                return (
                    <div key={credit.id} {...creditItemProps}>
                        {renderTextField('name', credit.name, settings.nameCaps, {
                            ...dynamicStyle,
                            color: effectiveNameColor,
                            fontSize: `${effectiveDefaultFontSize}px`, // Apply correct scaled size
                            textAlign: effectiveTextAlign,
                            // [WRAP FIX] 'pre' = honor explicit line breaks, never auto-wrap.
                            // Credit lines must only break where the user breaks them; the
                            // export container width can no longer force a second line.
                            whiteSpace: 'pre',
                            width: 'auto'
                        })}
                    </div>
                );
        }
    };

    // --- MAIN RENDER LOGIC ---
    const [exportWidth, exportHeight] = (resolution || settings.resolution || '1920x1080').split('x').map(Number);

    // EXPORTING: Render single card if specified
    if (exportType === 'cards' && isExporting) {
        const cardToRender = cardCredits[singleCardIndex];
        if (!cardToRender) return null;

        // Use absolute positioning to match preview behavior (top/left/transform: translate(-50%,-50%)).
        // This ensures cardPositionX/Y are honored in export, and textAlign only affects text justification.
        const posX = cardToRender.cardPositionX ?? 50;
        const posY = cardToRender.cardPositionY ?? 50;
        const cardContentStyle = {
            position: 'absolute',
            top: `${posY}%`,
            left: `${posX}%`,
            transform: 'translate(-50%, -50%)',
        };
        return (
            <div className="credits-card-container" style={{ width: `${exportWidth}px`, height: `${exportHeight}px`, backgroundColor: settings.backgroundColor, position: 'relative', overflow: 'hidden' }}>
                <div style={cardContentStyle}>
                    {renderCredit(cardToRender, true)}
                </div>
            </div>
        );
    }

    // Determine credits to render based on context (export type or preview)
    let creditsToRenderElements = [];
    if (isExporting) {
        if (exportType === 'full' || exportType === 'scroll') {
            creditsToRenderElements = credits.map(c => renderCredit(c, false));
        }
        // If exportType is 'cards', it's handled above. If 'full', card rendering happens separately in the export process.
    } else {
        // PREVIEW MODE: Render either cards or scroll credits based on EditorPage's state (implicitly via which arrays have content passed in props perhaps?)
        // Assuming EditorPage only passes *either* cardCredits *or* credits based on its internal logic for preview state.
        if (cardCredits && cardCredits.length > 0) {
            // Render the single active card passed via props (assuming EditorPage filters this)
            creditsToRenderElements = cardCredits.map(c => renderCredit(c, true));
        } else if (credits && credits.length > 0) {
            creditsToRenderElements = credits.map(c => renderCredit(c, false));
        }
    }


    if (creditsToRenderElements.length > 0) {
        const isCardPreviewMode = !isExporting && cardCredits && cardCredits.length > 0;
        const paneWidth = isCardPreviewMode ? previewPaneWidth : (measuredPaneWidth || previewPaneWidth);
        const stageZoom = (!isExporting && paneWidth > 0) ? paneWidth / PREVIEW_STAGE_WIDTH : 1;
        const containerStyle = {
            backgroundColor: settings.backgroundColor,
            minHeight: isExporting ? `${exportHeight}px` : '100%',
            // Preview scroll view: fixed stage width, zoomed to the pane (see
            // PREVIEW_STAGE_WIDTH). Card view keeps its shrink-to-fit width and
            // only zooms, so card positioning is unchanged.
            width: isExporting ? `${exportWidth}px` : (isCardPreviewMode || stageZoom === 1 ? '100%' : `${PREVIEW_STAGE_WIDTH}px`),
            ...(stageZoom !== 1 ? { zoom: stageZoom, flexShrink: 0 } : {}),
            // 8% Title Safe area on the left and right. In export this MUST be in
            // px: a % padding resolves against the PARENT's width, which in the
            // renderer is the page (viewport = frame x deviceScaleFactor 2), so
            // '8%' became 16% of the frame per side — the export's text area was
            // 68% of the frame vs 84% in the preview, and long names wrapped in
            // export that fit in the preview.
            padding: isExporting ? `0 ${Math.round(exportWidth * 0.08 * 100) / 100}px` : (isCardPreviewMode ? 0 : '0 8%'),
            // ^ Card preview: no side padding. The export card has none, and a %
            //   padding on this shrink-to-fit box squeezed it narrower than its
            //   longest line, pushing that line off-center (it overflowed to the
            //   right) compared to the render.
            boxSizing: 'border-box' // Ensures the padding pushes inward, not outward
        };
        return (
            <div
                ref={stageMeasureRef}
                className={`credits-container ${(isExporting || isCardPreviewMode || stageZoom === 1) ? 'w-full ' : ''}flex flex-col items-center`}
                style={containerStyle}
            >
                {creditsToRenderElements}
            </div>
        );
    }

    return null; // Render nothing if no credits are provided for the current context
};